import clsx from "clsx";
import {
  useSelect,
  SelectOptionDefinition,
  SelectProvider,
} from "@mui/base/useSelect";
import { useOption } from "@mui/base/useOption";
import { styled } from "@mui/system";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import { Stop } from "@mui/icons-material";
import { Box, SelectProps } from "@mui/material";
import { useRef, useState } from "react";

interface CustomSelectProps
  extends Omit<SelectProps, "placeholder" | "onChange" | "allow"> {
  onChange: (value: string | null, label?: string) => void;
  placeholder?: string;
  value?: string;
}

export default function CustomColorSelector({
  onChange,
  id,
  placeholder,
  value,
  ...rest
}: CustomSelectProps) {
  return (
    <CustomSelect
      {...rest}
      id={id}
      placeholder={placeholder}
      options={regColorOptions}
      onChange={onChange}
      selectValue={value}
    />
  );
}

const blue = {
  100: "#DAECFF",
  200: "#99CCF3",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Root = styled("div")`
  position: relative;
`;

const Toggle = styled("button")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  // min-width: 366px;
  width: 100%;
  height: 53px;
  padding: 8px 12px;
  border-radius: 4px;
  text-align: left;
  cursor: pointer;
  line-height: 1.5;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.23);
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  position: relative;

  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:focus-visible {
    outline: 0;
    border: 1px solid rgba(255, 255, 255, 1);
  }

  & > svg {
    font-size: 1rem;
    position: absolute;
    height: 100%;
    top: 0;
    right: 10px;
  }
  `,
);

const Listbox = styled("ul")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  min-height: calc(1.5em + 22px);
  max-height: 35vh;
  overflow: auto;
  min-width: 320px;
  padding: 12px;
  border-radius: 12px;
  text-align: left;
  line-height: 1.5;
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  padding: 5px;
  margin: 5px 0 0 0;
  position: absolute;
  height: auto;
  width: 100%;
  overflow: auto;
  z-index: 1;
  outline: 0px;
  list-style: none;
  box-shadow: 0px 2px 6px ${
    theme.palette.mode === "dark" ? "rgba(0,0,0, 0.50)" : "rgba(0,0,0, 0.05)"
  };

  &.hidden {
    opacity: 0;
    visibility: hidden;
    display: none;
    transition: opacity 0.4s ease, visibility 0.4s step-end;
  }
  `,
);

const Option = styled("li")(
  ({ theme }) => `
  padding: 8px;
  border-radius: 0.45em;

  &[aria-selected='true'] {
    background-color: ${theme.palette.mode === "dark" ? blue[900] : blue[100]};
    color: ${theme.palette.mode === "dark" ? blue[100] : blue[900]};
  }

  &.highlighted,
  &:hover {
    background-color: ${theme.palette.mode === "dark" ? grey[800] : grey[100]};
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  }

  &[aria-selected='true'].highlighted {
    background-color: ${theme.palette.mode === "dark" ? blue[900] : blue[100]};
    color: ${theme.palette.mode === "dark" ? blue[100] : blue[900]};
  }

  &:focus-visible {
    outline: 3px solid ${theme.palette.mode === "dark" ? blue[600] : blue[200]};
  }

  &::before {
    content: '';
    width: 1ex;
    height: 1ex;
    margin-right: 1ex;
    background-color: var(--color);
    display: inline-block;
    border-radius: 50%;
    vertical-align: middle;
  }
  `,
);

interface Props
  extends Omit<SelectProps, "placeholder" | "onChange" | "allow"> {
  options: SelectOptionDefinition<string>[];
  placeholder?: string;
  selectValue?: string;
  onChange: (value: string | null, label?: string) => void;
}

interface OptionProps {
  children?: React.ReactNode;
  className?: string;
  value: string;
  disabled?: boolean;
}

function renderSelectedValue(
  value: string | null,
  options: SelectOptionDefinition<string>[],
) {
  const selectedOption = options.find(
    (option) => option.value === value || option.label === value,
  );

  return selectedOption ? (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
      <Stop style={{ fill: selectedOption.value }} /> {selectedOption.label}
    </Box>
  ) : null;
}

function CustomOption(props: OptionProps) {
  const { children, value, className, disabled = false } = props;
  const { getRootProps, highlighted } = useOption({
    value,
    disabled,
    label: children,
  });

  return (
    <Option
      {...getRootProps()}
      className={clsx({ highlighted }, className)}
      style={{ "--color": value } as any}
    >
      {children}
    </Option>
  );
}

function CustomSelect({
  options,
  placeholder,
  id,
  selectValue,
  onChange,
}: Props) {
  const listboxRef = useRef<HTMLUListElement>(null);
  const [listboxVisible, setListboxVisible] = useState(false);

  const getColorLabel = (val: string | null) => {
    const foundItem = options.find((item) => item.value === val);
    return foundItem ? onChange(val, foundItem.label) : null;
  };

  const { getButtonProps, getListboxProps, contextValue, value } = useSelect<
    string,
    false
  >({
    listboxRef,
    onOpenChange: setListboxVisible,
    onChange: () => getColorLabel(value),
    open: listboxVisible,
  });

  return (
    <Root>
      <Toggle id={id} {...getButtonProps()} style={{ "--color": value } as any}>
        {renderSelectedValue(selectValue || value, options) || (
          <span className="placeholder">{placeholder ?? " "}</span>
        )}
        <UnfoldMoreRoundedIcon />
      </Toggle>
      <Listbox
        {...getListboxProps()}
        aria-hidden={!listboxVisible}
        className={listboxVisible ? "" : "hidden"}
      >
        <SelectProvider value={contextValue}>
          {options.map((option) => {
            return (
              <CustomOption key={option.value} value={option.value}>
                {option.label}
              </CustomOption>
            );
          })}
        </SelectProvider>
      </Listbox>
    </Root>
  );
}

export const regColorOptions = [
  {
    label: "Yellow",
    value: "#FDCB08",
  },
  {
    label: "Orange",
    value: "#FB8A2D",
  },
  {
    label: "Red",
    value: "#FE705D",
  },
  {
    label: "Rose",
    value: "#FD5E75",
  },
  {
    label: "Pink",
    value: "#F368B1",
  },
  {
    label: "Fuchsia",
    value: "#EA6CFE",
  },
  {
    label: "Violet",
    value: "#9F80FA",
  },
  {
    label: "Indigo",
    value: "#7783F8",
  },
  {
    label: "Blue",
    value: "#57A0FA",
  },
  {
    label: "Cyan",
    value: "#19C4E1",
  },
  {
    label: "Teal",
    value: "#16CAB4",
  },
  {
    label: "Emerald",
    value: "#2BCA90",
  },
  {
    label: "Green",
    value: "#3CD775",
  },
  {
    label: "Lime",
    value: "#A1D945",
  },
  {
    label: "Stone",
    value: "#9F9893",
  },
  {
    label: "Slate",
    value: "#8496AE",
  },
];
